import { CSSProperties, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { RoleModal } from "../entities/RoleTypeEditor";
import { useTranslation } from "react-i18next";
import { ModalShell } from "../../../atoms/ModalShell";
import { FaChevronDown, FaLock } from "react-icons/fa6";
import { RoleEditorType, UserRole } from "../../../../types/UserRolesTypes";
import { useUpdateRole } from "../../../../hooks/users";
import useAuth from "../../../../hooks/useAuth";
import { usePermissions } from "../../../../utils/permissions";
import { Cap, Level } from "../../../../utils/rbacCatalog";

export const RoleOptionsPart = ({
  data,
  customerId,
  entity_identification,
  user_identification,
  list_role,
  disabled,
}: RoleEditorType) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const canModifyRole = permissions.can(Cap.USERS_USER_ROLE, Level.WRITE);

  const [roles, setRoles] = useState<Array<UserRole>>(list_role);
  const [role, setRole] = useState<string>(data.role);
  const [showModalRole, setShowModalRole] = useState(false);

  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);

  const { mutate: updateRole } = useUpdateRole();

  const protectedRoles = ["OWNER", "LOYALTY_PROG_USER"];

  const prevCustomerIdRef = useRef<string>(customerId);

  useEffect(() => {
    setRoles(list_role);
  }, [list_role]);

  useEffect(() => {
    if (prevCustomerIdRef.current !== customerId) {
      setRole(data.role);
      prevCustomerIdRef.current = customerId;
    }
  }, [data, customerId]);

  useEffect(() => {
    if (!open) return;
    const positionMenu = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > window.innerHeight) {
        setOpen(false);
        return;
      }
      const gap = 4;
      const estimatedHeight = 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUp = spaceBelow < estimatedHeight && spaceAbove > spaceBelow;
      setMenuStyle({
        position: "fixed",
        left: rect.left,
        minWidth: rect.width,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + gap }
          : { top: rect.bottom + gap }),
      });
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    positionMenu();
    window.addEventListener("scroll", positionMenu, true);
    window.addEventListener("resize", positionMenu);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", positionMenu, true);
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleRoleManagement = (e: string) => {
    const optionValue = e;
    setOpen(false);

    if (optionValue !== "addRole") {
      updateRole(
        {
          user_identification: user_identification,
          entity_identification: entity_identification,
          customerId: customerId,
          role: optionValue,
        },
        {
          onSuccess: () => {
            setRole(optionValue);
          },
        }
      );
    } else {
      setShowModalRole(true);
    }
  };

  return (
    <div className="role-option-part-drop-down">
      <button
        type="button"
        ref={triggerRef}
        translate="no"
        disabled={!canModifyRole}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => canModifyRole && setOpen((o) => !o)}
        className={`group w-full justify-between items-center flex gap-1.5 ${
          !canModifyRole ? "cursor-not-allowed" : ""
        }`}
      >
        <div className="text-gray-500 text-sm font-normal font-figtree leading-tight text-left flex items-center gap-1.5 max-w-[150px]">
          {protectedRoles.includes(role) && (
            <FaLock
              size={14}
              className="text-amber-500 shrink-0"
              title="Protected role"
            />
          )}
          <span className="truncate">{role}</span>
        </div>
        {canModifyRole && (
          <div className="w-5 h-5 relative ml-1 group-hover:opacity-100 opacity-0">
            <FaChevronDown size={14} />
          </div>
        )}
      </button>

      {open &&
        createPortal(
          <>
            <div
              className="fixed inset-0 z-[9998]"
              onClick={() => setOpen(false)}
            />
            <div
              role="menu"
              translate="no"
              style={menuStyle}
              className="z-[9999] w-fit rounded-lg border border-gray-200 bg-white py-1 text-sm shadow-lg focus:outline-none"
            >
              <div className="max-h-[200px] overflow-y-auto">
                {roles.map((roleItem: UserRole, index: number) => (
                  <button
                    type="button"
                    role="menuitem"
                    key={index}
                    onClick={() => handleRoleManagement(roleItem.user_role)}
                    className="flex w-full items-center gap-1 whitespace-nowrap px-4 py-2 text-left text-gray-700 hover:bg-gray-100"
                  >
                    {protectedRoles.includes(roleItem.user_role) && (
                      <FaLock
                        size={14}
                        className="text-amber-500 shrink-0"
                        title="Protected role"
                      />
                    )}
                    <span>{roleItem.user_role}</span>
                  </button>
                ))}
              </div>
              <div className="my-1 h-px bg-gray-100" />
              <button
                type="button"
                role="menuitem"
                translate="no"
                onClick={() => handleRoleManagement("addRole")}
                className="flex w-full items-center whitespace-nowrap px-4 py-2 text-left text-blue-500 hover:bg-gray-100"
              >
                {t("dashboard.RoleTypeEditor.btn-02")}
              </button>
            </div>
          </>,
          document.body
        )}

      <ModalShell
        size="lg"
        show={showModalRole}
        onClose={() => setShowModalRole(false)}
        title={t("dashboard.RoleTypeEditor.modal.header-01")}
        subtitle={t("dashboard.RoleTypeEditor.modal.description-01")}
      >
        <RoleModal
          roles={roles}
          setRoles={setRoles}
          setShowRoleModal={setShowModalRole}
          setRole={setRole}
          user_identification={user_identification}
          entity_identification={entity_identification}
          customerId={customerId}
        />
      </ModalShell>
    </div>
  );
};
